/**
 * Terms of Service Page Component
 * Comprehensive terms for Lumi AI Figure Skating Training Assistant
 */

import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import './LegalPage.css';

function TermsOfService() {
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle anchor link clicks for table of contents
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <div className="legal-page">
      {/* SEO Meta Tags */}
      <SEO
        title="Terms of Service"
        description="Terms of Service for LumiSkating AI Figure Skating Assistant. Read our terms and conditions for using the platform."
        url="https://www.lumiskating.com/terms"
      />

      {/* Header */}
      <header className="legal-header">
        <div className="legal-nav">
          <Link to="/" className="legal-brand">
            <img src="/skate.png" alt="LumiSkating Logo" width="32" height="32" />
            <span>Lumi<span className="brand-skating">Skating</span></span>
          </Link>
          <button className="back-button" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left"></i>
            <span>Back</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="legal-content">
        <div className="legal-container">
          {/* Title Section */}
          <div className="legal-title-section">
            <h1>Terms of Service</h1>
            <p className="legal-effective-date">
              <strong>Effective Date:</strong> January 4, 2026
            </p>
          </div>

          {/* Table of Contents */}
          <nav className="legal-toc">
            <h2>Table of Contents</h2>
            <ol>
              <li><a href="#acceptance" onClick={(e) => scrollToSection(e, 'acceptance')}>Acceptance of Terms</a></li>
              <li><a href="#description" onClick={(e) => scrollToSection(e, 'description')}>Description of Service</a></li>
              <li><a href="#eligibility" onClick={(e) => scrollToSection(e, 'eligibility')}>Eligibility and Account Registration</a></li>
              <li><a href="#user-responsibilities" onClick={(e) => scrollToSection(e, 'user-responsibilities')}>User Responsibilities</a></li>
              <li><a href="#content-ownership" onClick={(e) => scrollToSection(e, 'content-ownership')}>Content Ownership and License</a></li>
              <li><a href="#ai-disclaimer" onClick={(e) => scrollToSection(e, 'ai-disclaimer')}>AI-Generated Content Disclaimer</a></li>
              <li><a href="#safety" onClick={(e) => scrollToSection(e, 'safety')}>Safety and Medical Disclaimer</a></li>
              <li><a href="#subscription" onClick={(e) => scrollToSection(e, 'subscription')}>Subscription and Payment</a></li>
              <li><a href="#prohibited" onClick={(e) => scrollToSection(e, 'prohibited')}>Prohibited Uses</a></li>
              <li><a href="#intellectual-property" onClick={(e) => scrollToSection(e, 'intellectual-property')}>Intellectual Property Rights</a></li>
              <li><a href="#privacy" onClick={(e) => scrollToSection(e, 'privacy')}>Privacy and Data Protection</a></li>
              <li><a href="#termination" onClick={(e) => scrollToSection(e, 'termination')}>Termination</a></li>
              <li><a href="#liability" onClick={(e) => scrollToSection(e, 'liability')}>Limitation of Liability</a></li>
              <li><a href="#indemnification" onClick={(e) => scrollToSection(e, 'indemnification')}>Indemnification</a></li>
              <li><a href="#changes" onClick={(e) => scrollToSection(e, 'changes')}>Changes to Terms</a></li>
              <li><a href="#governing-law" onClick={(e) => scrollToSection(e, 'governing-law')}>Governing Law</a></li>
              <li><a href="#contact" onClick={(e) => scrollToSection(e, 'contact')}>Contact Information</a></li>
            </ol>
          </nav>

          {/* Terms Content */}
          <div className="legal-body">
            <section id="acceptance">
              <h2>1. Acceptance of Terms</h2>
              <p>
                Welcome to Lumi ("we," "our," or "us"). By accessing or using the Lumi figure skating
                training assistant application and related services (collectively, the "Service"), you
                agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms,
                please do not use the Service.
              </p>
              <p>
                These Terms constitute a legally binding agreement between you and Lumi. By creating an
                account, accessing, or using any part of the Service, you acknowledge that you have read,
                understood, and agree to be bound by these Terms.
              </p>
            </section>

            <section id="description">
              <h2>2. Description of Service</h2>
              <p>
                Lumi is an AI-powered figure skating training assistant designed to help skaters improve
                their technique and performance. The Service includes, but is not limited to:
              </p>
              <ul>
                <li>
                  <strong>Video Analysis:</strong> Upload skating videos for AI-powered pose detection,
                  biomechanical analysis, and technique evaluation using MediaPipe and computer vision technology.
                </li>
                <li>
                  <strong>AI Coaching:</strong> Receive personalized coaching feedback, training recommendations,
                  and performance insights generated by artificial intelligence (Google Gemini).
                </li>
                <li>
                  <strong>Training Planning:</strong> Access tools for creating and managing training schedules,
                  goal setting, and progress tracking.
                </li>
                <li>
                  <strong>Mental Coaching:</strong> Guidance on mental preparation, competition readiness,
                  and psychological aspects of performance.
                </li>
                <li>
                  <strong>ISU Standards Reference:</strong> Information and comparisons based on International
                  Skating Union (ISU) technical standards and scoring criteria.
                </li>
              </ul>
            </section>

            <section id="eligibility">
              <h2>3. Eligibility and Account Registration</h2>

              <h3>3.1 Age Requirements</h3>
              <p>
                The Service is intended for users who are at least 13 years of age. If you are under 18 years
                of age, you must have the consent of a parent or legal guardian to use the Service. Parents or
                guardians who register on behalf of a minor assume full responsibility for the minor's use of
                the Service.
              </p>

              <h3>3.2 Account Registration</h3>
              <p>
                To access certain features of the Service, you must create an account. When registering, you agree to:
              </p>
              <ul>
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security of your password and account credentials</li>
                <li>Accept responsibility for all activities that occur under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>

              <h3>3.3 One Account Per User</h3>
              <p>
                Each user may maintain only one account. We reserve the right to terminate duplicate accounts
                or accounts created with fraudulent information.
              </p>
            </section>

            <section id="user-responsibilities">
              <h2>4. User Responsibilities</h2>
              <p>As a user of the Service, you are responsible for:</p>
              <ul>
                <li>
                  <strong>Physical Safety:</strong> Ensuring your physical safety during all skating activities.
                  The Service provides technical guidance but does not replace proper safety precautions,
                  appropriate supervision, or professional medical advice.
                </li>
                <li>
                  <strong>Professional Oversight:</strong> Consulting with qualified coaches, medical
                  professionals, and trainers as appropriate for your skill level and physical condition.
                </li>
                <li>
                  <strong>Content Accuracy:</strong> Ensuring that any videos or content you upload
                  accurately represent your skating and do not contain false or misleading information.
                </li>
                <li>
                  <strong>Appropriate Use:</strong> Using the Service only for its intended purpose of
                  figure skating training and improvement.
                </li>
                <li>
                  <strong>Equipment and Environment:</strong> Ensuring you have appropriate skating equipment,
                  safe ice conditions, and suitable training environments.
                </li>
              </ul>
            </section>

            <section id="content-ownership">
              <h2>5. Content Ownership and License</h2>

              <h3>5.1 Your Content</h3>
              <p>
                You retain ownership of all videos, images, and other content you upload to the Service
                ("User Content"). By uploading User Content, you grant Lumi a non-exclusive, worldwide,
                royalty-free license to:
              </p>
              <ul>
                <li>Process, analyze, and store your content to provide the Service</li>
                <li>Use anonymized and aggregated data derived from your content to improve our AI models
                and services</li>
                <li>Display your content back to you through the Service</li>
              </ul>

              <h3>5.2 Content Restrictions</h3>
              <p>You represent and warrant that your User Content:</p>
              <ul>
                <li>Does not violate any third party's intellectual property or privacy rights</li>
                <li>Does not contain inappropriate, offensive, or illegal content</li>
                <li>Features only yourself or individuals who have consented to being recorded and analyzed</li>
                <li>Does not contain minors without proper parental/guardian consent</li>
              </ul>

              <h3>5.3 Our Content</h3>
              <p>
                All AI-generated analysis, coaching feedback, training plans, and other content generated
                by the Service ("Generated Content") remains the property of Lumi. You are granted a
                personal, non-transferable license to use Generated Content for your personal training purposes.
              </p>
            </section>

            <section id="ai-disclaimer">
              <h2>6. AI-Generated Content Disclaimer</h2>
              <p>
                <strong>IMPORTANT:</strong> The Service uses artificial intelligence to analyze skating
                videos and provide coaching feedback. You acknowledge and agree that:
              </p>
              <ul>
                <li>
                  <strong>AI Limitations:</strong> AI-generated analysis and recommendations may contain
                  errors, inaccuracies, or limitations. The AI cannot fully assess all factors that a
                  human coach would consider.
                </li>
                <li>
                  <strong>Not a Replacement:</strong> AI coaching is intended to supplement, not replace,
                  instruction from qualified human coaches. Critical technique corrections and safety
                  assessments should always be verified by qualified professionals.
                </li>
                <li>
                  <strong>Biomechanical Estimates:</strong> Measurements such as jump height, rotation
                  speed, and body angles are estimates based on video analysis and may not be precise.
                  Factors such as video quality, camera angle, and lighting can affect accuracy.
                </li>
                <li>
                  <strong>ISU Standards Reference:</strong> References to ISU scoring and technical
                  standards are for educational purposes and may not reflect the most current rules
                  or official judging criteria.
                </li>
                <li>
                  <strong>Individual Variation:</strong> Recommendations are based on general biomechanical
                  principles and may not account for your individual physical characteristics, limitations,
                  or medical conditions.
                </li>
              </ul>
            </section>

            <section id="safety">
              <h2>7. Safety and Medical Disclaimer</h2>
              <p className="warning-box">
                <strong>WARNING:</strong> Figure skating is an inherently dangerous activity that can
                result in serious injury or death. Please read this section carefully.
              </p>

              <h3>7.1 Not Medical Advice</h3>
              <p>
                The Service does not provide medical advice. Any information about injuries, recovery,
                or physical training is for general informational purposes only and should not be
                considered medical advice. Always consult with qualified healthcare professionals
                before beginning any exercise program or continuing to skate after an injury.
              </p>

              <h3>7.2 Assumption of Risk</h3>
              <p>
                By using the Service, you acknowledge that figure skating involves inherent risks including,
                but not limited to:
              </p>
              <ul>
                <li>Falls resulting in injury</li>
                <li>Collision with other skaters or rink barriers</li>
                <li>Repetitive stress injuries</li>
                <li>Equipment-related injuries</li>
                <li>Cold-related conditions</li>
              </ul>
              <p>
                You voluntarily assume all risks associated with your skating activities, whether or not
                they result from following guidance provided by the Service.
              </p>

              <h3>7.3 Safety Monitoring</h3>
              <p>
                While the Service includes safety monitoring features that may identify potential injury
                risks or concerning patterns, these features are not comprehensive and should not be
                relied upon as the sole means of ensuring your safety.
              </p>
            </section>

            <section id="subscription">
              <h2>8. Subscription and Payment</h2>

              <h3>8.1 Free and Paid Features</h3>
              <p>
                The Service may offer both free and premium subscription tiers. Premium features require
                a paid subscription. Pricing and feature availability are subject to change.
              </p>

              <h3>8.2 Billing and Renewal</h3>
              <p>
                Paid subscriptions are billed in advance on a recurring basis (monthly or annually,
                depending on your selection). Your subscription will automatically renew unless you
                cancel before the renewal date.
              </p>

              <h3>8.3 Cancellation and Refunds</h3>
              <p>
                You may cancel your subscription at any time through your account settings. Cancellation
                will take effect at the end of the current billing period. We do not provide refunds for
                partial subscription periods, except as required by applicable law.
              </p>
            </section>

            <section id="prohibited">
              <h2>9. Prohibited Uses</h2>
              <p>You agree not to use the Service to:</p>
              <ul>
                <li>Upload content that infringes intellectual property rights or privacy rights of others</li>
                <li>Upload content containing nudity, sexually explicit material, or inappropriate content</li>
                <li>Upload videos of individuals without their consent</li>
                <li>Attempt to reverse engineer, decompile, or extract our AI models or algorithms</li>
                <li>Use the Service for any commercial purpose without authorization</li>
                <li>Share your account credentials with others or allow others to access your account</li>
                <li>Circumvent any access controls or usage limits</li>
                <li>Use the Service to compete with or develop competing products</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>
            </section>

            <section id="intellectual-property">
              <h2>10. Intellectual Property Rights</h2>
              <p>
                The Service, including its original content, features, functionality, AI models, and
                technology, is owned by Lumi and is protected by international copyright, trademark,
                patent, trade secret, and other intellectual property laws.
              </p>
              <p>
                Our trademarks, service marks, and logos may not be used without our prior written consent.
                All third-party trademarks, including "ISU" and "International Skating Union," are the
                property of their respective owners.
              </p>
            </section>

            <section id="privacy">
              <h2>11. Privacy and Data Protection</h2>
              <p>
                Your privacy is important to us. Our collection and use of personal information is governed
                by our <Link to="/privacy">Privacy Policy</Link>, which is incorporated into these Terms by
                reference. By using the Service, you consent to our collection and use of data as described
                in the Privacy Policy.
              </p>
            </section>

            <section id="termination">
              <h2>12. Termination</h2>

              <h3>12.1 Termination by You</h3>
              <p>
                You may terminate your account at any time by contacting us or using the account deletion
                feature in your settings. Upon termination, your right to use the Service will immediately cease.
              </p>

              <h3>12.2 Termination by Us</h3>
              <p>
                We may suspend or terminate your account and access to the Service at any time, with or
                without cause, and with or without notice. Reasons for termination may include, but are
                not limited to:
              </p>
              <ul>
                <li>Violation of these Terms</li>
                <li>Fraudulent, abusive, or illegal activity</li>
                <li>Non-payment of fees</li>
                <li>Extended periods of inactivity</li>
                <li>Requests by law enforcement</li>
              </ul>

              <h3>12.3 Effect of Termination</h3>
              <p>
                Upon termination, your User Content may be deleted. We are not obligated to retain or
                provide copies of your content after termination.
              </p>
            </section>

            <section id="liability">
              <h2>13. Limitation of Liability</h2>
              <p>
                <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong>
              </p>
              <ul>
                <li>
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                  WHETHER EXPRESS, IMPLIED, OR STATUTORY.
                </li>
                <li>
                  WE DISCLAIM ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
                  FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </li>
                <li>
                  WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
                </li>
                <li>
                  IN NO EVENT SHALL LUMI BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
                  OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
                </li>
                <li>
                  OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM THESE TERMS OR YOUR USE OF THE SERVICE
                  SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM,
                  OR $100, WHICHEVER IS GREATER.
                </li>
                <li>
                  WE ARE NOT LIABLE FOR ANY INJURIES, DAMAGES, OR LOSSES RESULTING FROM YOUR SKATING
                  ACTIVITIES, WHETHER OR NOT THEY RELATE TO GUIDANCE PROVIDED BY THE SERVICE.
                </li>
              </ul>
            </section>

            <section id="indemnification">
              <h2>14. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Lumi and its officers, directors,
                employees, agents, and affiliates from and against any claims, liabilities, damages,
                losses, costs, or expenses (including reasonable attorneys' fees) arising from:
              </p>
              <ul>
                <li>Your use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any rights of another party</li>
                <li>Your User Content</li>
                <li>Your skating activities</li>
              </ul>
            </section>

            <section id="changes">
              <h2>15. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. If we make material changes,
                we will notify you by email or through the Service before the changes take effect.
                Your continued use of the Service after the effective date of any changes constitutes
                your acceptance of the modified Terms.
              </p>
              <p>
                We encourage you to review these Terms periodically for any changes.
              </p>
            </section>

            <section id="governing-law">
              <h2>16. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the
                jurisdiction in which Lumi operates, without regard to its conflict of law provisions.
                Any disputes arising from these Terms or your use of the Service shall be resolved
                through binding arbitration or in the courts of competent jurisdiction.
              </p>
            </section>

            <section id="contact">
              <h2>17. Contact Information</h2>
              <p>
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="contact-info">
                <p><strong>Email:</strong> legal@lumiskating.com</p>
                <p><strong>Address:</strong> Lumi Figure Skating Training</p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="acknowledgment">
              <p>
                By using the Lumi Service, you acknowledge that you have read, understood, and agree
                to be bound by these Terms of Service.
              </p>
            </section>
          </div>

          {/* Related Links */}
          <div className="legal-related">
            <h3>Related Documents</h3>
            <div className="related-links">
              <Link to="/privacy" className="related-link">
                <i className="fas fa-shield-alt"></i>
                <span>Privacy Policy</span>
              </Link>
              <Link to="/" className="related-link">
                <i className="fas fa-home"></i>
                <span>Return to Home</span>
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="legal-footer">
        <div className="legal-footer-content">
          <p>&copy; 2026 LumiSkating. All rights reserved.</p>
          <div className="legal-footer-links">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default TermsOfService;
